import React, { useState, useEffect } from 'react';
import { useFormState } from '../utils';

// Enums/Constants
const PLACE_OF_SERVICE_OPTIONS = ['Office', 'Home', 'Birth-Center'];
const FETAL_POSITION_OPTIONS = ['LOA', 'ROA', 'Transverse', 'LOP', 'ROP', 'LSA', 'RSA', 'Other'];
const VISIT_STATUS_OPTIONS = ['R', 'C'];
const RISK_ASSESSMENT_OPTIONS = ['Appropriate for midwifery care', 'Requires consultation', 'Requires referral'];
const SOAP_STATUS_OPTIONS = ['None', 'WNL', 'Adequate', 'Yes', 'No', 'NA'];

export default function PrenatalVisitForm({ 
  visit, 
  onChange, 
  onSave, 
  visitNumber,
  gestationalAge,
  pregnancy,
  saveStatus = "saved" 
}) {
  const [showStatus, setShowStatus] = useState(false);
  const [activeTab, setActiveTab] = useState("identifiers");

  // Transform backend data to frontend format
  useEffect(() => {
    if (visit?.Fetuses?.length > 0) {
      const transformedVisit = {
        ...visit,
        // Transform snake_case to camelCase
        visitDate: visit.VisitDate,
        gestAgeWeeks: visit.GestAgeWeeks,
        gestAgeDays: visit.GestAgeDays,
        weightLbs: visit.MaternalWeightLbs,
        bpSystolic: visit.BP_Systolic,
        bpDiastolic: visit.BP_Diastolic,
        fundalHeightCm: visit.FundalHeightCm,
        notes: visit.Notes
      };

      // Handle fetal data
      if (pregnancy?.isTwins) {
        const twinA = visit.Fetuses.find(f => f.FetusLabel === "A");
        const twinB = visit.Fetuses.find(f => f.FetusLabel === "B");

        if (twinA) {
          transformedVisit.fetalHeartTonesTwinA = twinA.HeartRateBpm;
          transformedVisit.fetalPositionTwinA = twinA.Presentation;
          transformedVisit.fetalMovementTwinA = twinA.Notes;
        }

        if (twinB) {
          transformedVisit.fetalHeartTonesTwinB = twinB.HeartRateBpm;
          transformedVisit.fetalPositionTwinB = twinB.Presentation;
          transformedVisit.fetalMovementTwinB = twinB.Notes;
        }
      } else {
        const fetus = visit.Fetuses[0];
        if (fetus) {
          transformedVisit.fetalHeartTones = fetus.HeartRateBpm;
          transformedVisit.fetalPosition = fetus.Presentation;
          transformedVisit.fetalMovement = fetus.Notes;
        }
      }

      // Update the form state
      onChange({
        target: {
          name: 'visit',
          value: transformedVisit
        }
      });
    }
  }, [visit?.Fetuses]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    await onSave(visit);
  };

  // Validation helper
  const validateForm = () => {
    const requiredFields = [
      'visitDate',
      'gestAgeWeeks',
      'weightLbs',
      'bpSystolic',
      'bpDiastolic',
      'renderingProviderName'
    ];

    const missingFields = requiredFields.filter(field => !visit[field]);
    if (missingFields.length > 0) {
      // Show error message
      return false;
    }
    return true;
  };

  const TabButton = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 ${
        activeTab === id
          ? "border-emerald-600 text-emerald-700"
          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );

  const renderIdentifiersTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Visit Number</label>
        <div className="text-lg font-semibold text-gray-900">{visitNumber}</div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Visit Date*</label>
        <input
          type="date"
          name="visitDate"
          value={visit.visitDate || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Place of Service*</label>
        <select
          name="placeOfService"
          value={visit.placeOfService || ""}
          onChange={onChange}
          required
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        >
          <option value="">Select Location</option>
          {PLACE_OF_SERVICE_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Gestational Age*</label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="number"
              name="gestAgeWeeks"
              value={visit.gestAgeWeeks || ""}
              onChange={onChange}
              min="0"
              max="45"
              required
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              placeholder="Weeks"
            />
          </div>
          <div className="flex-1">
            <input
              type="number"
              name="gestAgeDays"
              value={visit.gestAgeDays || ""}
              onChange={onChange}
              min="0"
              max="6"
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              placeholder="Days"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Provider Face to Face Minutes</label>
        <input
          type="number"
          name="providerFaceToFaceMinutes"
          value={visit.providerFaceToFaceMinutes || ""}
          onChange={onChange}
          min="0"
          max="300"
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>
    </div>
  );

  const renderVitalsTab = () => (
    <div className="space-y-6">
      {/* Maternal Measurements */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Maternal Measurements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs)*</label>
            <input
              type="number"
              name="weightLbs"
              value={visit.weightLbs || ""}
              onChange={onChange}
              step="0.1"
              required
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Blood Pressure*</label>
            <div className="flex gap-2">
              <input
                type="number"
                name="bpSystolic"
                value={visit.bpSystolic || ""}
                onChange={onChange}
                required
                placeholder="Systolic"
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              />
              <span className="text-gray-500 self-center">/</span>
              <input
                type="number"
                name="bpDiastolic"
                value={visit.bpDiastolic || ""}
                onChange={onChange}
                required
                placeholder="Diastolic"
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pulse (bpm)</label>
            <input
              type="number"
              name="pulse"
              value={visit.pulse || ""}
              onChange={onChange}
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature (°F)</label>
            <input
              type="number"
              name="temperatureF"
              value={visit.temperatureF || ""}
              onChange={onChange}
              step="0.1"
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fundal Height (cm)</label>
            <input
              type="number"
              name="fundalHeightCm"
              value={visit.fundalHeightCm || ""}
              onChange={onChange}
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            />
          </div>
        </div>
      </div>

      {/* Fetal Measurements */}
      {pregnancy?.isTwins ? (
        // Twin measurements
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Twin A */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Twin A Measurements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Position</label>
                <select
                  name="fetalPositionTwinA"
                  value={visit.fetalPositionTwinA || ""}
                  onChange={onChange}
                  className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                >
                  <option value="">Select Position</option>
                  {FETAL_POSITION_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Heart Tones (bpm)</label>
                <input
                  type="number"
                  name="fetalHeartTonesTwinA"
                  value={visit.fetalHeartTonesTwinA || ""}
                  onChange={onChange}
                  className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Movement</label>
                <select
                  name="fetalMovementTwinA"
                  value={visit.fetalMovementTwinA || ""}
                  onChange={onChange}
                  className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                >
                  <option value="">Select Status</option>
                  {SOAP_STATUS_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Twin B */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Twin B Measurements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Position</label>
                <select
                  name="fetalPositionTwinB"
                  value={visit.fetalPositionTwinB || ""}
                  onChange={onChange}
                  className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                >
                  <option value="">Select Position</option>
                  {FETAL_POSITION_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Heart Tones (bpm)</label>
                <input
                  type="number"
                  name="fetalHeartTonesTwinB"
                  value={visit.fetalHeartTonesTwinB || ""}
                  onChange={onChange}
                  className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Movement</label>
                <select
                  name="fetalMovementTwinB"
                  value={visit.fetalMovementTwinB || ""}
                  onChange={onChange}
                  className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
                >
                  <option value="">Select Status</option>
                  {SOAP_STATUS_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Single baby measurements
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Fetal Measurements</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Position</label>
              <select
                name="fetalPosition"
                value={visit.fetalPosition || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Position</option>
                {FETAL_POSITION_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Heart Tones (bpm)</label>
              <input
                type="number"
                name="fetalHeartTones"
                value={visit.fetalHeartTones || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Movement</label>
              <select
                name="fetalMovement"
                value={visit.fetalMovement || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Status</option>
                {SOAP_STATUS_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderUrinalysisTab = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <input
          type="checkbox"
          name="uaPerformed"
          checked={visit.uaPerformed || false}
          onChange={(e) => onChange({
            target: {
              name: 'uaPerformed',
              value: e.target.checked
            }
          })}
          className="h-4 w-4 text-emerald-600"
        />
        <label className="ml-2 text-sm text-gray-700">Urinalysis Performed</label>
      </div>

      {visit.uaPerformed && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: 'uaProteinMgDl', label: 'Protein (mg/dL)' },
            { name: 'uaGlucoseMgDl', label: 'Glucose (mg/dL)' },
            { name: 'uaKetones', label: 'Ketones' },
            { name: 'uaLeukocytes', label: 'Leukocytes' },
            { name: 'uaNitrites', label: 'Nitrites' },
            { name: 'uaBlood', label: 'Blood' },
            { name: 'uaPH', label: 'pH' },
            { name: 'uaSpecificGravity', label: 'Specific Gravity' }
          ].map(field => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
              <input
                type="text"
                name={field.name}
                value={visit[field.name] || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderReviewTab = () => (
    <div className="space-y-6">
      {/* Maternal Review */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Maternal Review</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: 'headaches', label: 'Headaches' },
            { name: 'nutrition', label: 'Nutrition' },
            { name: 'hydration', label: 'Hydration' },
            { name: 'bowelMovements', label: 'Bowel Movements' },
            { name: 'vaginalSymptoms', label: 'Vaginal Symptoms' },
            { name: 'visualDisturbances', label: 'Visual Disturbances' },
            { name: 'edema', label: 'Edema' },
            { name: 'nausea', label: 'Nausea' },
            { name: 'emesis', label: 'Emesis' }
          ].map(field => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
              <select
                name={field.name}
                value={visit[field.name] || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Status</option>
                {SOAP_STATUS_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Fetal Review */}
      {pregnancy?.isTwins ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Twin A Review */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Twin A Review</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Activity</label>
              <select
                name="fetalActivityTwinA"
                value={visit.fetalActivityTwinA || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Status</option>
                {SOAP_STATUS_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Twin B Review */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Twin B Review</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Activity</label>
              <select
                name="fetalActivityTwinB"
                value={visit.fetalActivityTwinB || ""}
                onChange={onChange}
                className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
              >
                <option value="">Select Status</option>
                {SOAP_STATUS_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Fetal Review</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fetal Activity</label>
            <select
              name="fetalActivity"
              value={visit.fetalActivity || ""}
              onChange={onChange}
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            >
              <option value="">Select Status</option>
              {SOAP_STATUS_OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Seen Other Provider</label>
        <select
          name="seenOtherProvider"
          value={visit.seenOtherProvider || ""}
          onChange={onChange}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        >
          <option value="">Select</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>
    </div>
  );

  const renderAssessmentTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Risk Assessment</label>
        <select
          name="riskAssessment"
          value={visit.riskAssessment || ""}
          onChange={onChange}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        >
          <option value="">Select Assessment</option>
          {RISK_ASSESSMENT_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (SOAP Format)</label>
        <textarea
          name="notes"
          value={visit.notes || ""}
          onChange={onChange}
          rows={6}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
          placeholder="Enter SOAP notes..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Plan & Next Steps</label>
        <textarea
          name="planNextSteps"
          value={visit.planNextSteps || ""}
          onChange={onChange}
          rows={4}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
          placeholder="Enter plan and next steps..."
        />
      </div>
    </div>
  );

  const renderSignatureTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Provider Name*</label>
          <input
            type="text"
            name="renderingProviderName"
            value={visit.renderingProviderName || ""}
            onChange={onChange}
            required
            className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Provider Credentials</label>
          <input
            type="text"
            name="renderingProviderCredentials"
            value={visit.renderingProviderCredentials || ""}
            onChange={onChange}
            className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            placeholder="e.g., CPM CDEM"
          />
        </div>
      </div>

      {visit.signedTimestamp && (
        <div className="text-sm text-gray-500">
          Signed on: {new Date(visit.signedTimestamp).toLocaleString()}
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow">
      {/* Status Messages */}
      {showStatus && saveStatus === "saved" && (
        <div className="text-green-700 bg-green-100 border border-green-300 rounded-t-xl px-3 py-2">
          Visit saved successfully
        </div>
      )}
      {showStatus && saveStatus === "error" && (
        <div className="text-red-700 bg-red-100 border border-red-300 rounded-t-xl px-3 py-2">
          Error saving visit
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-2 px-4">
          <TabButton id="identifiers" label="Visit Info" />
          <TabButton id="vitals" label="Vitals & Exam" />
          <TabButton id="urinalysis" label="Urinalysis" />
          <TabButton id="review" label="Review" />
          <TabButton id="assessment" label="Assessment" />
          <TabButton id="signature" label="Signature" />
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === "identifiers" && renderIdentifiersTab()}
        {activeTab === "vitals" && renderVitalsTab()}
        {activeTab === "urinalysis" && renderUrinalysisTab()}
        {activeTab === "review" && renderReviewTab()}
        {activeTab === "assessment" && renderAssessmentTab()}
        {activeTab === "signature" && renderSignatureTab()}
      </div>

      {/* Save Button */}
      <div className="flex justify-end p-4 bg-gray-50 rounded-b-xl border-t">
        <button
          type="submit"
          disabled={saveStatus === "saving"}
          className={`px-4 py-2 rounded text-white text-sm font-medium ${
            saveStatus === "saving"
              ? "bg-yellow-400"
              : saveStatus === "error"
              ? "bg-red-500"
              : "bg-emerald-600 hover:bg-emerald-700"
          }`}
        >
          {saveStatus === "saving" ? "Saving..." : "Save Visit"}
        </button>
      </div>
    </form>
  );
} 